import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

export interface Assistant {
  id: string;
  name: string;
  description?: string;
  model?: string;
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  status: 'active' | 'inactive' | 'training' | 'creating' | string;
  folderId?: string;
  organizationId?: string;
  channels?: string[];
  color?: string;
  messagesHandled?: number;
  successRate?: number;
  responseTime?: number;
  createdAt: string;
  _count?: {
    conversations: number;
    documents: number;
  };
}

interface AssistantState {
  assistants: Assistant[];
  currentAssistant: Assistant | null;
  loading: boolean;
  error: string | null;
}

const initialState: AssistantState = {
  assistants: [],
  currentAssistant: null,
  loading: false,
  error: null,
};

export const fetchAssistants = createAsyncThunk(
  'assistants/fetchAssistants',
  async (params?: { folderId?: string; status?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.folderId && params.folderId !== 'all') searchParams.set('folderId', params.folderId);
    if (params?.status && params.status !== 'all') searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    const response = await api.get(`/assistants${qs ? `?${qs}` : ''}`);
    return response.data.assistants as Assistant[];
  }
);

// Create assistant (Blank Canvas / FlowBuilder base)
export const createAssistant = createAsyncThunk(
  'assistants/createAssistant',
  async (
    payload: {
      name: string;
      description?: string;
      systemPrompt?: string;
      model?: string;
      temperature?: number;
      maxTokens?: number;
      voiceEnabled?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/assistants', {
        name: payload.name,
        description: payload.description || '',
        systemPrompt:
          payload.systemPrompt ||
          `You are ${payload.name}, an AI assistant. Be helpful, concise, and professional.`,
        model: payload.model || 'gpt-4-turbo-preview',
        temperature: payload.temperature ?? 0.7,
        maxTokens: payload.maxTokens ?? 1000,
        voiceEnabled: payload.voiceEnabled ?? false,
      }, { headers: { 'Idempotency-Key': `assist_create_${Date.now()}` } });

      if (response.data?.assistant) {
        return response.data.assistant as Assistant; // covers 201 and 202 cases
      }
      throw new Error('Assistant creation failed: invalid response');
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || e.message || 'Failed to create assistant');
    }
  }
);

// Clone assistant by ID (Import with ID)
export const cloneAssistant = createAsyncThunk(
  'assistants/cloneAssistant',
  async (assistantId: string) => {
    const response = await api.post(`/assistants/${assistantId}/clone`);
    return response.data.assistant as Assistant;
  }
);

// Generate assistant via AI PowerBuilder (minimal params path)
export const generateAssistant = createAsyncThunk(
  'assistants/generateAssistant',
  async (payload: {
    assistantName: string;
    description?: string;
  }) => {
    // Map name/description to builder schema with safe defaults
    const response = await api.post(
      '/assistant-builder/generate',
      {
        assistantName: payload.assistantName,
        businessType: 'other',
        primaryFunction: 'general',
        tone: 'friendly',
        features: [],
        integrations: [],
        customRequirements: payload.description || '',
      },
      { headers: { 'Idempotency-Key': `assist_gen_${Date.now()}` } }
    );

    // Endpoint may return 201 with full assistant or 202 with job info + minimal assistant
    if (response.status === 201 && response.data.assistant) {
      return response.data.assistant as Assistant;
    }
    if (response.status === 202 && response.data.assistant) {
      return response.data.assistant as Assistant; // temporary minimal entry
    }
    throw new Error('Assistant generation failed');
  }
);

const assistantSlice = createSlice({
  name: 'assistants',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAssistantsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    addAssistant: (state, action: PayloadAction<Assistant>) => {
      const exists = state.assistants.some(a => a.id === action.payload.id);
      if (!exists) {
        state.assistants = [action.payload, ...state.assistants];
      } else {
        state.assistants = state.assistants.map(a => (a.id === action.payload.id ? action.payload : a));
      }
    },
    updateAssistant: (state, action: PayloadAction<Assistant>) => {
      const idx = state.assistants.findIndex(a => a.id === action.payload.id);
      if (idx !== -1) state.assistants[idx] = action.payload;
      if (state.currentAssistant?.id === action.payload.id) {
        state.currentAssistant = action.payload;
      }
    },
    removeAssistant: (state, action: PayloadAction<string>) => {
      state.assistants = state.assistants.filter(a => a.id !== action.payload);
      if (state.currentAssistant?.id === action.payload) {
        state.currentAssistant = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssistants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssistants.fulfilled, (state, action) => {
        state.loading = false;
        state.assistants = action.payload || [];
      })
      .addCase(fetchAssistants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch assistants';
      })
      .addCase(createAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssistant.fulfilled, (state, action) => {
        state.loading = false;
        const created = action.payload;
        const exists = state.assistants.some(a => a.id === created.id);
        state.assistants = exists ? state.assistants.map(a => (a.id === created.id ? created : a)) : [created, ...state.assistants];
        state.currentAssistant = created;
      })
      .addCase(createAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to create assistant';
      })
      .addCase(cloneAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cloneAssistant.fulfilled, (state, action) => {
        state.loading = false;
        const cloned = action.payload;
        const exists = state.assistants.some(a => a.id === cloned.id);
        state.assistants = exists ? state.assistants.map(a => (a.id === cloned.id ? cloned : a)) : [cloned, ...state.assistants];
        state.currentAssistant = cloned;
      })
      .addCase(cloneAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to clone assistant';
      })
      .addCase(generateAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateAssistant.fulfilled, (state, action) => {
        state.loading = false;
        const generated = action.payload;
        const exists = state.assistants.some(a => a.id === generated.id);
        state.assistants = exists ? state.assistants.map(a => (a.id === generated.id ? generated : a)) : [generated, ...state.assistants];
        state.currentAssistant = generated;
      })
      .addCase(generateAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to generate assistant';
      });
  },
});

export const { clearError, addAssistant, updateAssistant, removeAssistant, setAssistantsLoading } = assistantSlice.actions;
export default assistantSlice.reducer;